import { CreateRealEstate, RealEstate, RealEstateGeometry, v2Report } from 'src/interfaces/realEstate.interface'
import { coreAPI, gatewayAPI } from './base.service'
import {
  DroneViewLink,
  GeoJsonCoordinates,
  GeoJsonPoint,
  Orthophoto,
  OutdoorPlan,
  UploadDroneviewLink,
  UploadOrtho,
  UploadOutdoorDrawing
} from '@/interfaces/misc.interface'

export function getAllRealEstates(): Promise<RealEstate[]> {
  const path = `/v1/realestates`
  const req = coreAPI.get(path)
  return req.then((res) => res?.data)
}
/**
 * delete real estate
 * @param id real estate id
 */
export async function deleteRealEstate(id: string): Promise<void> {
  const path = `/v1/realestates/${id}`
  await coreAPI.delete(path)
}
/**
 * get geoJson geometry for real estate
 * @param id real estate id
 * @returns geoJson for real estate
 */
export function getRealEstateGeoJson(id: string): Promise<RealEstateGeometry> {
  const path = `/v1/realestates/${id}/geojson`
  const req = coreAPI.get(path)
  return req.then((res) => res?.data)
}
/**
 * Creates a new real estate in backend
 * @param body name, place and realmId
 * @returns created real estate
 */
export async function createNewRealEstate(body: CreateRealEstate): Promise<RealEstate | undefined> {
  const path = `/v1/realestates`
  try {
    const response = await coreAPI.post(path, body)
    return response?.data
  } catch (error) {
    return undefined
  }
}
/**
 * update realEstate with new info
 * @param id realEstate id
 * @param realEstate updated realEstate object
 */
export async function updateRealEstate(id: string, realEstate: RealEstate): Promise<RealEstate | undefined> {
  const path = `/v1/realestates/${id}`
  try {
    const response = await coreAPI.put(path, realEstate)
    return response?.data
  } catch (error) {
    return undefined
  }
}

/**
 * update realEstate GeoJSON with new info
 * @param id realEstate GeoJSON id
 * @param realEstateGeoJSON coordinates to update realEstate GeoJSON object
 */
export async function updateRealEstateGeometry(
  id: string,
  geometryCoordinates: GeoJsonCoordinates
): Promise<RealEstateGeometry | undefined> {
  const path = `/v1/realestates/${id}/geojson`
  try {
    let geometryType = ''
    if (typeof geometryCoordinates[0][0][0] === 'number') {
      geometryType = 'Polygon'
    } else {
      geometryType = 'MultiPolygon'
    }
    const realEstateGeoJSON = { coordinates: geometryCoordinates, type: geometryType }
    const response = await coreAPI.post(path, realEstateGeoJSON)
    return response?.data
  } catch (error) {
    return undefined
  }
}
/**
 * Post file to backend
 * @param path url path
 * @param formData formdata with file and object
 * @returns formdata
 */
export async function postFile(path: string, formData: FormData) {
  const headers = {
    'Content-Type': 'multipart/form-data'
  }
  try {
    const data = await coreAPI.post(path, formData, {
      headers: headers
    })
    return data
  } catch (e) {
    console.error(e)
  }
}
/**
 * Add outdoor plan to real estate
 * @param id real estate id
 * @param outdoorPlan outdoor plan object
 * @param file  file to upload
 * @returns outdoor plan object
 */
export async function addRealEstateOutdoorDrawing(
  id: string,
  outdoorPlan: UploadOutdoorDrawing,
  file: File
): Promise<OutdoorPlan | undefined> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('outdoorPlan', new Blob([JSON.stringify(outdoorPlan)], { type: 'application/json' }))
  const path = `/v1/realestates/${id}/outdoorplans`
  try {
    const response = await postFile(path, formData)
    return response?.data
  } catch (error) {
    return undefined
  }
}
export type UpdateOutdoorDrawing = {
  name?: string
  description?: string
  active?: boolean
  northWest?: GeoJsonPoint
  northEast?: GeoJsonPoint
  southEast?: GeoJsonPoint
  southWest?: GeoJsonPoint
  title?: string
  type?: string
}
export async function updateOutdoorDrawing(realEstateId: string, drawingId: string, body: UpdateOutdoorDrawing) {
  const path = `/v1/realestates/${realEstateId}/outdoorplans/${drawingId}`
  const formData = new FormData()
  formData.append('planPatch', new Blob([JSON.stringify(body)], { type: 'application/json' }))
  try {
    const response = await coreAPI.patch(path, formData)
    return response?.data
  } catch (error) {
    return undefined
  }
}
/**
 * Add orthophoto to real estate
 * @param id real estate id
 * @param orthophoto orthophoto object
 * @param file  file to upload
 * @returns orthophoto object
 */
export async function addRealEstateOrthophoto(
  id: string,
  orthophoto: UploadOrtho,
  file: File
): Promise<Orthophoto | undefined> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('orthophoto', new Blob([JSON.stringify(orthophoto)], { type: 'application/json' }))
  const path = `/v1/realestates/${id}/orthophotos`
  try {
    const response = await postFile(path, formData)
    return response?.data
  } catch (error) {
    return undefined
  }
}
export type UpdateOrthophoto = {
  name?: string
  description?: string
  active?: boolean
  northWest?: GeoJsonPoint
  northEast?: GeoJsonPoint
  southEast?: GeoJsonPoint
  southWest?: GeoJsonPoint
  title?: string
}
/**
 * Update orthophoto
 * @param realEstateId real estate id
 * @param orthophotoId orthophoto id
 * @param body orthophoto object updated values
 * @returns orthophoto object
 */
export async function updateRealEstateOrthophoto(
  realEstateId: string,
  orthophotoId: string,
  body: UpdateOrthophoto
): Promise<Orthophoto | undefined> {
  const path = `/v1/realestates/${realEstateId}/orthophotos/${orthophotoId}`
  const formData = new FormData()
  formData.append('orthophotoPatch', new Blob([JSON.stringify(body)], { type: 'application/json' }))
  try {
    const response = await coreAPI.patch(path, formData)
    return response?.data
  } catch (error) {
    return undefined
  }
}
/**
 * Get v2 report, currently used to fetch area measurements
 * @param id entity id
 * @returns V2Report
 */
export async function getV2Report(id: string): Promise<v2Report> {
  const path = `core/v2/reports/${id}`
  const req = gatewayAPI.get(path)
  return req.then((res) => res?.data)
}

/**
 * add droneviewlink to real estate
 * @param id real estate id
 * @param body droneviewlink object
 */
export async function addDroneviewLink(id: string, body: UploadDroneviewLink): Promise<DroneViewLink | undefined> {
  const path = `/v1/realestates/${id}/droneviewlinks`
  try {
    const response = await coreAPI.post(path, body)
    return response?.data
  } catch (error) {
    return undefined
  }
}
export type UpdateDroneviewLink = {
  title?: string
  link?: string
  active?: boolean
  version?: number
  order?: number
}
/**
 * Update reality capture data
 * @param realEstateId real estate id
 * @param linkId reality capture id
 * @param body object with updated values
 * @returns reality capture object
 */
export async function updateRealEstateDroneviewLink(
  realEstateId: string,
  linkId: string,
  body: UpdateDroneviewLink
): Promise<DroneViewLink | undefined> {
  const path = `/v1/realestates/${realEstateId}/droneviewlinks/${linkId}`
  try {
    const response = await coreAPI.patch(path, body)
    return response?.data
  } catch (error) {
    return undefined
  }
}

export async function deleteDrawing(realEstateId: string, id: string): Promise<boolean> {
  const path = `/v1/realestates/${realEstateId}/outdoorplans/${id}`
  try {
    await coreAPI.delete(path)
    return true
  } catch (error) {
    return false
  }
}

export async function deleteOrthophoto(realEstateId: string, id: string): Promise<boolean> {
  const path = `/v1/realestates/${realEstateId}/orthophotos/${id}`
  try {
    await coreAPI.delete(path)
    return true
  } catch (error) {
    return false
  }
}

export async function deleteDroneviewLink(realEstateId: string, id: string): Promise<boolean> {
  const path = `/v1/realestates/${realEstateId}/droneviewlinks/${id}`
  try {
    await coreAPI.delete(path)
    return true
  } catch (error) {
    return false
  }
}
